import { type WebhookPreset } from '@littlebird/sdk';

import { Icons } from '@/assets/icons/icons';
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu/dropdown-menu';

import { WebhookServiceLogo } from './webhook-service-logo';

const groupByService = (presets: WebhookPreset[]) => {
  const groups = new Map<string, WebhookPreset[]>();
  for (const preset of presets) {
    groups.set(preset.service, [...(groups.get(preset.service) ?? []), preset]);
  }
  return [...groups.entries()];
};

/** "GitHub pull requests" reads as "Pull requests" in GitHub's own submenu. */
const eventLabel = (preset: WebhookPreset) => {
  const prefix = `${preset.service} `;
  if (!preset.label.startsWith(prefix)) return preset.label;
  const rest = preset.label.slice(prefix.length);
  return rest.charAt(0).toUpperCase() + rest.slice(1);
};

/** Services in a menu, with a submenu of event kinds for a service that has more than one preset. */
export function WebhookPresetMenu({
  presets,
  selected,
  onSelect,
}: {
  presets: WebhookPreset[];
  selected: WebhookPreset | null;
  onSelect: (preset: WebhookPreset) => void;
}) {
  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger
        variant="outline"
        aria-label="Start from a service"
        className="w-full"
      >
        <span className="flex min-w-0 items-center gap-2">
          {selected ? (
            <WebhookServiceLogo service={selected.service} className="size-4" />
          ) : null}
          <span className="truncate">
            {selected?.label ?? 'Start from a service'}
          </span>
        </span>
        <Icons.chevronDown className="size-3.5 text-foreground-muted" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        {groupByService(presets).map(([service, servicePresets]) => {
          const [only] = servicePresets;
          if (servicePresets.length === 1 && only) {
            return (
              <DropdownMenuItem key={service} onSelect={() => onSelect(only)}>
                <WebhookServiceLogo service={service} />
                {only.label}
              </DropdownMenuItem>
            );
          }
          return (
            <DropdownMenuSub key={service}>
              <DropdownMenuSubTrigger>
                <WebhookServiceLogo service={service} />
                {service}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {servicePresets.map((preset) => (
                  <DropdownMenuItem
                    key={preset.key}
                    onSelect={() => onSelect(preset)}
                  >
                    {eventLabel(preset)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
